/**
 * Suspension detector.
 *
 * Fires `app/resumed` when wall-clock time advances far more than the
 * timer that measured it should allow. A `delay(1000)` that took eight
 * seconds means the process was not running for seven of them — host
 * sleep, tab freeze, or the tab being throttled to the 1/minute budget
 * Chrome gives background pages.
 *
 * This exists because `visibilitychange` cannot see the worst case: a
 * laptop that sleeps with the tab in the *foreground*. Nothing about the
 * document's visibility changes across that suspension, yet every socket
 * the page held is gone — precisely the state that produced sync cycles
 * where all five requests aborted together with `reached_server: false`.
 */

import { SagaIterator } from 'redux-saga';
import { delay, put } from 'redux-saga/effects';
import { appResumed } from './actions';

/** How often we check the clock. */
export const RESUME_TICK_MS = 1000;

/**
 * Gap that counts as a suspension. Comfortably above the jitter a busy
 * event loop or a slow render can add to a 1s timer, and below the ~60s
 * granularity of a throttled background tab, so an ordinary hiccup never
 * triggers a cycle.
 */
export const RESUME_GAP_MS = 5000;

/**
 * `now` is injectable so tests can drive the clock instead of sleeping.
 */
export function* resumeDetectorSaga(now: () => number = Date.now): SagaIterator {
  let last = now();
  while (true) {
    yield delay(RESUME_TICK_MS);
    const current = now();
    const gap = current - last;
    last = current;
    // Guard against a clock stepped backwards (NTP correction after a
    // resume): a negative gap is not evidence of a suspension.
    if (gap >= RESUME_GAP_MS) {
      yield put(appResumed(gap));
    }
  }
}
